import { useState, useEffect } from "react";
import { toast } from "react-toastify";

interface DateInputProps {
  onSelect: (value: string | null) => void;
  defaultValue?: string | null;
}

export const DateInput = ({ onSelect, defaultValue }: DateInputProps) => {

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Validate that date string is complete (YYYY-MM-DD format)
  const isValidDate = (dateString: string): boolean => {
    if (!dateString) return false;

    // Check format: must be exactly 10 characters (YYYY-MM-DD)
    if (dateString.length !== 10) return false;

    // Check that it matches YYYY-MM-DD pattern
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    // Check that the date is actually valid (e.g., not 2024-02-31)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;

    // Check that the date components match what was entered
    // (prevents things like "2024-02-31" being auto-corrected to March)
    const parts = dateString.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    return (
      date.getFullYear() === year &&
      date.getMonth() + 1 === month &&
      date.getDate() === day
    );
  };

  const [value, setValue] = useState<string>(formatDateForInput(defaultValue));
  const [error, setError] = useState<string | null>(null);

  // Initialize the parent's inputValue when component mounts
  useEffect(() => {
    if (!defaultValue) {
      onSelect(null);
    } else {
      try {
        const date = new Date(defaultValue);
        if (!isNaN(date.getTime())) {
          onSelect(date.toISOString());
        } else {
          onSelect(null);
        }
      } catch {
        onSelect(null);
      }
    }
  }, []); // Only run on mount

  useEffect(() => {
    setValue(formatDateForInput(defaultValue));
    setError(null);
  }, [defaultValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setError(null);

    // If empty, clear the value
    if (!newValue) {
      onSelect(null);
      return;
    }

    // Validate the date
    if (!isValidDate(newValue)) {
      setError('Voer een complete datum in (jaar, maand, dag)');
      toast.error('Voer een complete datum in (jaar, maand, dag)');
      onSelect(null);
      return;
    }

    // Valid date - convert to ISO string
    try {
      const date = new Date(newValue);
      const isoString = date.toISOString();
      setError(null);
      onSelect(isoString);
    } catch (err) {
      setError('Ongeldige datum');
      toast.error('Ongeldige datum');
      onSelect(null);
    }
  };

  return (
    <div className="p-4" onClick={(e) => e.stopPropagation()}>
      <input
        type="date"
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => e.stopPropagation()}
        autoFocus
        required
        pattern="\d{4}-\d{2}-\d{2}"
        title="Voer een volledige datum in (jaar-maand-dag)"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-sm transition-colors ${
          error
            ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
            : 'border-neutral-primary/20 focus:ring-moss-primary focus:border-transparent'
        }`}
      />
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};